import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import { useNavigate } from "react-router-dom";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MuiAvatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import "../i18n/i18n";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useColorScheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import KEYCLOAK_CONFIG from "../config/keycloakConfig";

// Glassmorphism Efektli Kart
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(5),
  gap: theme.spacing(3),
  margin: "auto",
  maxWidth: "480px",
  borderRadius: "24px",
  backdropFilter: "blur(20px)",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(30, 30, 40, 0.7)"
      : "rgba(255, 255, 255, 0.8)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
      : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  // Giriş animasyonu
  animation: "fadeInUp 0.8s ease-out",
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  width: 28,
  height: 28,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.secondary,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
}));

// Açık Yeşil ve Beyaz Gradyan Arka Plan
const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "100dvh",
  minHeight: "100%",
  padding: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  background:
    theme.palette.mode === "dark"
      ? // Dark mode için koyu yeşilimsi gri
        "radial-gradient(at 50% 0%, hsla(150, 20%, 20%, 1) 0, transparent 70%), radial-gradient(at 50% 100%, hsla(150, 25%, 10%, 1) 0, transparent 70%)"
      : // Light mode - Açık yeşil ve beyaz gradyan
        `
      radial-gradient(at 50% 50%, #ffffff 0%, transparent 60%),
      radial-gradient(at 0% 0%, hsla(145, 60%, 96%, 1) 0, transparent 50%),
      radial-gradient(at 100% 100%, hsla(145, 60%, 94%, 1) 0, transparent 50%)
    `,
  backgroundColor: theme.palette.mode === "dark" ? "#0d1f0d" : "#f0fdf4",
}));

function parseJwt(token) {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT format");
  const base64 =
    parts[1].replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice(2 - (parts[1].length % 4));
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
  return JSON.parse(json);
}

export default function SignIn(props) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { tenantConfig } = props;

  const [language, setLanguage] = React.useState(i18n.language || "tr");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [dialogMessage, setDialogMessage] = React.useState("");
  const [keycloakLoading, setKeycloakLoading] = React.useState(false);


  // Code'un sadece bir kez işlenmesini sağlamak için ref
  const codeProcessedRef = React.useRef(false);

  const showErrorDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };


  // Keycloak ile giriş yap (Login)
  const handleKeycloakLogin = () => {
    // Signup değil, normal login olduğunu işaretle
    sessionStorage.setItem("keycloak_flow", "login");
    const uiLocale = localStorage.getItem("i18nextLng") || "en";
    const authUrl = `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/auth?client_id=${KEYCLOAK_CONFIG.clientId}&response_type=code&redirect_uri=${encodeURIComponent(KEYCLOAK_CONFIG.redirectUri)}&scope=openid&ui_locales=${uiLocale}`;
    window.location.href = authUrl;
  };

  // Keycloak ile kayıt ol (Sign Up)
  const handleKeycloakSignup = () => {
    // Signup olduğunu işaretle
    sessionStorage.setItem("keycloak_flow", "signup");
    // Keycloak register sayfasına yönlendir
    const uiLocale = localStorage.getItem("i18nextLng") || "en";
    const registerUrl = `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/registrations?client_id=${KEYCLOAK_CONFIG.clientId}&response_type=code&redirect_uri=${encodeURIComponent(KEYCLOAK_CONFIG.redirectUri)}&scope=openid&ui_locales=${uiLocale}`;
    window.location.href = registerUrl;
  };

  // Keycloak'tan dönen authorization code'u işle
  const handleKeycloakCallback = async (code) => {
    setKeycloakLoading(true);

    try {
      // 1. Authorization code ile token al
     

      const tokenParams = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: KEYCLOAK_CONFIG.clientId,
        code: code,
        redirect_uri: KEYCLOAK_CONFIG.redirectUri,
      });

      const tokenResponse = await fetch(
        `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: tokenParams,
        },
      );

      const tokenData = await tokenResponse.json();

      if (!tokenData.access_token) {
        throw new Error(tokenData.error_description || "Token alınamadı");
      }

      const token = tokenData.access_token;

      // Keycloak logout için id_token'ı sakla
      if (tokenData.id_token) {
        localStorage.setItem("idToken", tokenData.id_token);
      }

      // Token'ı localStorage'a kaydet
      localStorage.setItem("authToken", token);
      if (tokenData.refresh_token) {
        localStorage.setItem("refreshToken", tokenData.refresh_token);
      }

      // Flow tipini temizle
      sessionStorage.removeItem("keycloak_flow");

      // URL'den code parametresini temizle
      window.history.replaceState({}, document.title, window.location.pathname);

      // pendingConfirmRole varsa, confirmRole sayfasına yönlendir
      const pendingConfirmRole = localStorage.getItem("pendingConfirmRole");
      if (pendingConfirmRole) {
        try {
          const crData = JSON.parse(pendingConfirmRole);
          localStorage.removeItem("pendingConfirmRole");
          const crParams = new URLSearchParams();
          if (crData.projectCode) crParams.set("projectCode", crData.projectCode);
          if (crData.adminName) crParams.set("adminName", crData.adminName);
          if (crData.role) crParams.set("role", crData.role);
          if (crData.expireTime) crParams.set("expireTime", crData.expireTime);
          if (crData.securityCode) crParams.set("code", crData.securityCode);
          navigate(`/confirmRole?${crParams.toString()}`);
          return;
        } catch (e) {
          localStorage.removeItem("pendingConfirmRole");
        }
      }

      // Client seçim sayfasına yönlendir
      navigate("/client");
    } catch (error) {
      console.error("Keycloak login error:", error);
      showErrorDialog(
        "Keycloak Giriş Hatası",
        error.message || "Keycloak ile giriş yapılamadı",
      );
    } finally {
      setKeycloakLoading(false);
    }
  };

  // On page load, force MUI color mode to light (once)
  function SystemModeSync() {
    const { setMode } = useColorScheme();
    React.useEffect(() => {
      setMode("light");
    }, [setMode]);
    return null;
  }

  // Token geçerlilik kontrolü - expire olmuş mu?
  const isTokenExpired = (token) => {
    try {
      const decoded = parseJwt(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch (e) {
      return true; // Token parse edilemezse geçersiz kabul et
    }
  };

  // localStorage'ı temizle
  const clearAuthData = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("adminId");
      localStorage.removeItem("objectData");
      localStorage.removeItem("enumData");
      localStorage.removeItem("userRoles");
      localStorage.removeItem("projectCode");
      localStorage.removeItem("companyName");
    } catch (e) {
      console.warn("localStorage clear failed", e);
    }
  };

  // Sayfa yüklendiğinde token kontrolü ve Keycloak callback işleme
  React.useEffect(() => {
    // URL'den authorization code kontrolü
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");

    if (code) {
      // iframe içinde silent auth callback mi?
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: "keycloak-silent-auth", code },
          window.location.origin
        );
        return;
      }

      // Code zaten işlendiyse tekrar işleme (React StrictMode için)
      if (codeProcessedRef.current) {
        console.log("Code already processed, skipping...");
        return;
      }
      codeProcessedRef.current = true;

      // URL'den code'u hemen temizle (duplicate işlemeyi önlemek için)
      window.history.replaceState({}, document.title, window.location.pathname);

      // Keycloak'tan döndük, code'u işle
      handleKeycloakCallback(code);
      return;
    }

    // iframe içinde silent auth error (SSO session dolmuş)
    if (error && window.parent !== window) {
      window.parent.postMessage(
        { type: "keycloak-silent-auth", error },
        window.location.origin
      );
      return;
    }

    // Code yok - token kontrolü yap
    const existingToken = localStorage.getItem("authToken");

    if (existingToken) {
      // Token var - geçerli mi kontrol et
      if (isTokenExpired(existingToken)) {
        // Token expire olmuş - temizle, login sayfasını göster
        console.log("Token expired, clearing auth data...");
        clearAuthData();
        // Login sayfası gösterilecek, kullanıcı butona basacak
      } else {
        // Token geçerli - dashboard'a git
        console.log("Valid token found, redirecting to dashboard...");
        navigate("/dashboard");
      }
    }
    // Token yoksa login sayfası gösterilir, kullanıcı Keycloak butonuna basar
  }, []);

  // Dil seçiminin ilk yüklemede doğru görünmesi için useEffect ekleyelim
  React.useEffect(() => {
    if (i18n.language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  const turkeyFlag = require("../assets/turkey.png");
  const ukFlag = require("../assets/united-kingdom.png");
  const germanFlag = require("../assets/german.png");
  const frenchFlag = require("../assets/france.png");
  const spanishFlag = require("../assets/es.png");

  // Determine tenant logo (supports both top-level and theme-scoped fields)
  const tenantLogoLight =
    tenantConfig?.logoLight || tenantConfig?.theme?.logoLight || null;
  const tenantLogoDark =
    tenantConfig?.logoDark || tenantConfig?.theme?.logoDark || null;
  const resolvedLogoSrc =
    theme.palette.mode === "dark"
      ? tenantLogoDark || tenantLogoLight
      : tenantLogoLight || tenantLogoDark;

  return (
    <AppTheme {...props}>
      {/* Force color mode to follow the OS on this page */}
      <SystemModeSync />
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        {/* Dil Seçici - Sağ Üst Köşede Sabit */}
        <Stack
          direction="row"
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            zIndex: 10,
            gap: 1,
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={(event, newLanguage) => {
              if (newLanguage !== null) {
                setLanguage(newLanguage);
                i18n.changeLanguage(newLanguage);
                // Mark this as an explicit user choice so the RCU English
                // default no longer applies on subsequent loads.
                localStorage.setItem("langSelected", "1");
              }
            }}
            aria-label="language selection"
            size="small"
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "12px",
              border: "none",
              "& .MuiToggleButtonGroup-grouped": {
                minWidth: "40px",
                padding: "8px 10px",
                border: "none",
                borderRadius: "10px !important",
                margin: "4px",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              },
            }}
          >
            <ToggleButton value="tr" aria-label="Turkish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Turkish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={turkeyFlag}
                    alt="Turkish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  TR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="en" aria-label="English">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="English" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={ukFlag}
                    alt="UK Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  EN
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="de" aria-label="German">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="German" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={germanFlag}
                    alt="German Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  DE
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="fr" aria-label="French">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="French" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={frenchFlag}
                    alt="French Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  FR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="es" aria-label="Spanish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Spanish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={spanishFlag}
                    alt="Spanish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  ES
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Card variant="outlined" sx={{ position: "relative" }}>
          {/* Loading Overlay - Blur efekti */}
          {keycloakLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(30, 30, 40, 0.7)"
                    : "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: theme.palette.primary.main,
                }}
              />
              <Typography
                sx={{
                  mt: 2,
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {t("signIn.keycloakProcessing") || "Giriş yapılıyor..."}
              </Typography>
            </Box>
          )}
          {/* Proje İsmi - En Üstte ve Büyük */}
          {tenantConfig?.name && (
            <Typography
              variant="h2"
              fontWeight="800"
              sx={{
                color:
                  theme.palette.mode === "dark"
                    ? "#FFF"
                    : theme.palette.primary.main,
                textShadow:
                  theme.palette.mode === "dark"
                    ? "0px 2px 4px rgba(0, 0, 0, 0.2)"
                    : "none",
                transition: "color 0.3s ease",
                textAlign: "center",
                letterSpacing: "0.02em",
                mb: 3,
                ...(tenantConfig.textStyle || {}),
              }}
            >
              {tenantConfig.name}
            </Typography>
          )}

          {/* Logo (varsa) */}
          {resolvedLogoSrc && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                filter: "drop-shadow(0px 4px 6px rgba(0,0,0,0.1))",
              }}
            >
              <Box
                component="img"
                src={resolvedLogoSrc}
                alt={`${tenantConfig?.name || "Logo"} Logo`}
                sx={{
                  height: 80,
                  objectFit: "contain",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.05)" },
                }}
              />
            </Box>
          )}

          {/* Hoşgeldiniz Mesajı */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                textAlign: "center",
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
              }}
            >
              {t("signIn.welcome") || "Hoş Geldiniz"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                textAlign: "center",
                mt: 1,
              }}
            >
              {t("signIn.welcomeDescription") ||
                "Devam etmek için giriş yapın veya hesap oluşturun"}
            </Typography>
          </Box>

          {/* Butonlar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
              mt: 2,
            }}
          >
            {/* Giriş Yap Butonu */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleKeycloakLogin}
              disabled={keycloakLoading}
              startIcon={!keycloakLoading && <LoginIcon />}
              sx={{
                py: 1.8,
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "12px",
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)",
                  transform: "scale(1.02)",
                  boxShadow: "0 5px 10px 3px rgba(33, 203, 243, .4)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(45deg, #90CAF9 30%, #B2EBF2 90%)",
                },
              }}
            >
              {keycloakLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("signIn.keycloakLogin") || "Giriş Yap"
              )}
            </Button>

            {/* Şık Divider */}
            <Divider
              sx={{
                my: 1,
                "&::before, &::after": {
                  borderColor: theme.palette.divider,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  px: 2,
                  fontSize: "0.85rem",
                }}
              >
                {t("signIn.or") || "veya"}
              </Typography>
            </Divider>

            {/* Kayıt Ol Butonu */}
            <Button
              fullWidth
              variant="outlined"
              onClick={handleKeycloakSignup}
              disabled={keycloakLoading}
              startIcon={!keycloakLoading && <PersonAddIcon />}
              sx={{
                py: 1.8,
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "12px",
                borderWidth: 2,
                borderColor: "#4CAF50",
                color: "#4CAF50",
                backgroundColor: "rgba(76, 175, 80, 0.05)",
                transition:
                  "transform 0.2s, background-color 0.2s, border-color 0.2s",
                "&:hover": {
                  borderWidth: 2,
                  borderColor: "#388E3C",
                  backgroundColor: "rgba(76, 175, 80, 0.12)",
                  transform: "scale(1.02)",
                },
                "&:disabled": {
                  borderColor: "#A5D6A7",
                  color: "#A5D6A7",
                },
              }}
            >
              {keycloakLoading ? (
                <CircularProgress size={24} color="success" />
              ) : (
                t("signIn.keycloakSignup") || "Kayıt Ol"
              )}
            </Button>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${theme.palette.divider}`,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: "block",
              }}
            >
              {t("signIn.copyright") ||
                `© ${new Date().getFullYear()} LettaIoT. Tüm hakları saklıdır.`}
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
      {/* Error Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography id="error-dialog-description">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            color="primary"
            autoFocus
          >
            {t("common.ok") || "OK"}
          </Button>
        </DialogActions>
      </Dialog>

    </AppTheme>
  );
}
